import { yupResolver } from "@hookform/resolvers/yup";
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Grid,
    MenuItem,
    Stack,
    Switch,
    Typography,
} from "@mui/material";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { AuthContext } from "../../../contexts/AuthContext";
import { CONFIG } from "../../../services/config";

import { FormInputText } from "../../../components/form/FormInputText";
import MyModal from "../../../components/MyModal";
import ActionButtons from "../../../components/MyModal/ActionButtons";

type Props = {
  isEdition?: boolean;
  onClose: Function;
  openSucessQuickAlert: Function;
  openErrorQuickAlert: Function;
  messageQuickAlert: Function;
  totalPositions: number;
  bannerData?: null | {
    id: Number;
    internalLinkId: Number | null;
    name: string;
    description: string;
    image: any;
    typeLink: string;
    redirectLink: string | null;
    displayOrder: string;
    active: boolean;
  };
};

export interface FormValues {
  name: string;
  description: string;
  image: any;
  typeLink: string;
  redirectLink: string | null;
  displayOrder: string;
  active: boolean;
}

const styles = {
  titleModal: { lineHeight: 0.3, fontSize: 16 },
  subTitleModal: { lineHeight: 0.3, fontSize: 12 },
  alignCenter: { display: "flex", alignItems: "center" },
  imageContent: { width: 130, height: 50, marginLeft: "16px" },
  buttonContent: { display: "flex", alignItems: "center" },
  buttonItem: { marginRight: "12px" },
};

export default function ModalBannerControl({
  isEdition = false,
  onClose,
  openSucessQuickAlert,
  openErrorQuickAlert,
  messageQuickAlert,
  totalPositions,
  bannerData,
}: Props) {
  const { t } = useTranslation();
  const { tokenHeaders } = useContext(AuthContext);
  const typeLinkList = [
    t("banners.modal.linkTypeOption1"),
    t("banners.modal.linkTypeOption2"),
  ];

  const [loading, setLoading] = useState(false);
  const [displayOrderList, setDisplayOrderList] = useState([""]);
  const [hasEditionImage, setHasEditionImage] = useState(false);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [selectDropdown, setSelectDropdown] = useState<string | null>(null);
  const [internalLinkList, setInternalLinkList] = useState<any>(null);
  const [selectedIdInternalLink, setSelectedIdInternalLink] =
    useState<Number>();
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const formData = yup.object().shape({
    name: isEdition
      ? yup.string()
      : yup.string().required(t("banners.required.name")),
    typeLink: yup.string().required(t("banners.required.typeLink")),
    redirectLink: yup
      .string()
      .nullable()
      .required(t("banners.required.redirectLink")),
    displayOrder: yup.string().required(t("banners.required.displayOrder")),
    image: yup
      .mixed()
      .test("required-image", t("banners.required.image"), function (value) {
        return isEdition || value?.length > 0;
      }),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(formData),
    context: { isEdition },
  });

  const convertImageToBase64 = (event: any) => {
    const file = event.target.files![0];

    if (!file.name.match(/\.(jpg|jpeg)$/i)) {
      setValue("image", null);
      messageQuickAlert(t("banners.quickAlert.modalBannerErrorImageType"));
      openErrorQuickAlert();
      return;
    }
    if (file.size > 1000000) {
      setValue("image", null);
      messageQuickAlert(t("banners.quickAlert.modalBannerErrorImageSize"));
      openErrorQuickAlert();
      return;
    }
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();
      img.src = reader.result as string;
      setHasEditionImage(true);

      img.onload = () => {
        if (img.width !== 1496 || img.height !== 560) {
          setValue("image", null);
          messageQuickAlert(t("banners.quickAlert.modalBannerErrorImageType"));
          openErrorQuickAlert();
          return;
        }

        const base64 = reader.result?.toString().split(",")[1] || null;
        setImageBase64(base64);
        setValue("image", base64, { shouldValidate: true });
      };
    };
    reader.readAsDataURL(file);
  };

  const imageUrlToBase64 = async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();

      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const createOrderList = () => {
    if (isEdition) {
      const arrayOrderList: any = Array.from(
        { length: totalPositions + 1 },
        (_, index: any) => {
          return index < 10 ? `0${index}` : index.toString();
        },
      );

      setDisplayOrderList(arrayOrderList.splice(1, totalPositions + 1));
    } else {
      setDisplayOrderList(["01"]);
    }
  };

  const checkImageUrlToEdit = () => {
    if (isEdition) {
      imageUrlToBase64(bannerData?.image)
        .then((base64String: any) => {
          setImageBase64(base64String.toString().split(",")[1]);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const HorizontalLine = (
    <Box
      sx={{
        borderTop: "1px solid var(--gray-100)",
        marginBottom: 3,
        marginTop: 2,
      }}
    />
  );

  const sendFormBanner: SubmitHandler<FormValues> = async (values) => {
    setLoading(true);
    const isInternalLink: boolean =
      values.typeLink === t("banners.modal.linkTypeOption1");

    const data: object = {
      name: isEdition ? bannerData?.name : values.name,
      description: values.description,
      image: isEdition
        ? hasEditionImage
          ? imageBase64
          : bannerData?.image
        : imageBase64,
      internalLinkId: isInternalLink ? selectedIdInternalLink : null,
      isInternalLink,
      redirectLink: values.redirectLink,
      displayOrder: parseInt(values.displayOrder, 10),
      active: values.active,
    };

    if (isEdition) {
      try {
        await axios.put(
          `${CONFIG.API_URL}banners/${bannerData?.id}`,
          {
            ...data,
            changeImage: hasEditionImage,
          },
          tokenHeaders,
        );
        messageQuickAlert(t("banners.quickAlert.modalEditBannerSuccess"));
        openSucessQuickAlert();
      } catch (error: any) {
        if (error.status === 409) {
          messageQuickAlert(t("banners.quickAlert.modalBannerErrorName"));
        } else {
          messageQuickAlert(t("banners.quickAlert.modalEditBannerError"));
        }
        openErrorQuickAlert();
      } finally {
        onClose();
      }
    } else {
      try {
        await axios.post(`${CONFIG.API_URL}banners`, data, tokenHeaders);
        messageQuickAlert(t("banners.quickAlert.modalCreateBannerSuccess"));
        openSucessQuickAlert();
      } catch (error: any) {
        if (error.status === 409) {
          messageQuickAlert(t("banners.quickAlert.modalBannerErrorName"));
        } else {
          messageQuickAlert(t("banners.quickAlert.modalCreateBannerError"));
        }
        openErrorQuickAlert();
      } finally {
        onClose();
      }
    }
    setLoading(false);
  };

  const deleteBanner = async () => {
    setLoading(true);
    try {
      await axios.delete(
        `${CONFIG.API_URL}banners/${bannerData?.id}`,
        tokenHeaders,
      );
      messageQuickAlert(t("banners.quickAlert.modalDeleteBannerSuccess"));
      openSucessQuickAlert();
    } catch (error: any) {
      messageQuickAlert(t("banners.quickAlert.modalDeleteBannerError"));
      openErrorQuickAlert();
    } finally {
      onClose();
    }
    setLoading(false);
  };

  const getInternalLinks = async () => {
    if (isEdition) {
      if (bannerData?.typeLink === typeLinkList[0]) {
        setSelectDropdown(typeLinkList[0]);
      } else {
        setSelectDropdown(typeLinkList[1]);
      }
    }

    try {
      const response: any = await axios.get(
        `${CONFIG.API_URL}bannerInternalLinks?size=${999999}`,
        tokenHeaders,
      );

      response.data.content.length === 0
        ? setInternalLinkList(null)
        : setInternalLinkList(response.data.content);
    } catch (error: any) {
      onClose();
      messageQuickAlert("internal error");
      openErrorQuickAlert();
    }
  };

  useEffect(() => {
    getInternalLinks();
    createOrderList();
    checkImageUrlToEdit();
  }, []);

  return (
    <MyModal
      maxWidth="62rem"
      title={
        isEdition
          ? t("banners.modal.titleModalEdit")
          : t("banners.modal.titleModalAdd")
      }
      modalChange={onClose}
      component="form"
      onSubmit={handleSubmit(sendFormBanner)}
    >
      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <p style={styles.titleModal}>
            {t("banners.modal.identificationLabel")}
          </p>
        </Grid>

        <Grid item md={5} xs={12}>
          <FormInputText
            id="name"
            type="text"
            label={t("banners.modal.identificationNameLabel")}
            defaultValue={bannerData?.name}
            inputProps={{
              maxLength: 100,
              minLength: 1,
            }}
            {...register("name")}
            error={errors?.name}
            disabled={loading || isEdition}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <FormInputText
            id="description"
            label={t("banners.modal.identificationDescLabel")}
            defaultValue={bannerData?.description}
            inputProps={{
              maxLength: 100,
              minLength: 1,
            }}
            {...register("description")}
            disabled={loading}
          />
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <p style={styles.titleModal}>{t("banners.modal.imageTitle")}</p>
          <p style={styles.subTitleModal}>
            {t("banners.modal.imageDimensionSubtitle")}
          </p>
          <p style={styles.subTitleModal}>
            {t("banners.modal.imageTypeSubtitle")}
          </p>
          <p style={styles.subTitleModal}>
            {t("banners.modal.imageMaxSizeSubtitle")}
          </p>
        </Grid>
        <Grid item md={5} xs={12} sx={styles.alignCenter}>
          <label htmlFor="image-upload">
            <Button variant="contained" component="label">
              {t("banners.modal.selectImageLabel")}
              <input
                id="image-upload"
                type="file"
                accept=".jpg, .jpeg"
                hidden
                {...register("image")}
                onChange={convertImageToBase64}
                disabled={loading}
              />
            </Button>
          </label>

          {errors.image?.message && (
            <Box
              sx={{
                color: "var(--red-100)",
                fontSize: "14px",
                marginLeft: "14px",
              }}
            >
              {String(errors.image.message)}
            </Box>
          )}
        </Grid>

        <Grid item md={4} xs={12} sx={styles.alignCenter}>
          <Box display="flex" alignItems="center">
            <p style={styles.titleModal}>
              {t("banners.modal.currentImageSubtitle")}
            </p>
            <div style={styles.imageContent}>
              {imageBase64 || bannerData?.image ? (
                <img
                  src={
                    imageBase64
                      ? `data:image/jpeg;base64,${imageBase64}`
                      : bannerData?.image
                  }
                  alt="icon"
                  style={{ width: "100%", objectFit: "cover" }}
                />
              ) : null}
            </div>
          </Box>
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <p style={styles.titleModal}>{t("banners.modal.redirectionTitle")}</p>
          <p style={styles.subTitleModal}>
            {t("banners.modal.redirectionSubtitle")}
          </p>
        </Grid>
        <Grid item md={5} xs={12}>
          <FormInputText
            select
            id="typeLink"
            label={t("banners.modal.linkTypeLabel")}
            defaultValue={bannerData?.typeLink}
            {...register("typeLink")}
            error={errors?.typeLink}
            disabled={loading}
          >
            {typeLinkList.map((type) => (
              <MenuItem
                key={type}
                value={type}
                onClick={() => {
                  setSelectDropdown(type);
                  setValue("redirectLink", null);
                }}
              >
                {type}
              </MenuItem>
            ))}
          </FormInputText>
        </Grid>

        {selectDropdown && (
          <Grid item md={4} xs={12}>
            {selectDropdown === t("banners.modal.linkTypeOption1") ? (
              internalLinkList && (
                <FormInputText
                  select
                  id="redirectLink"
                  label={t("banners.modal.linkInternalLabel")}
                  defaultValue={bannerData?.redirectLink}
                  {...register("redirectLink")}
                  error={errors?.redirectLink}
                  disabled={loading}
                >
                  {internalLinkList.map((internalLink: any) => (
                    <MenuItem
                      key={internalLink.id}
                      value={internalLink.link}
                      onClick={() => setSelectedIdInternalLink(internalLink.id)}
                    >
                      {internalLink.name}
                    </MenuItem>
                  ))}
                </FormInputText>
              )
            ) : (
              <FormInputText
                id="redirectLink"
                label={t("banners.modal.linkExternalLabel")}
                defaultValue={bannerData?.redirectLink}
                inputProps={{
                  maxLength: 2000,
                  minLength: 1,
                }}
                {...register("redirectLink")}
                error={errors?.redirectLink}
                disabled={loading}
              />
            )}
          </Grid>
        )}
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <p style={styles.titleModal}>
            {t("banners.modal.positionCarroselTitle")}
          </p>
        </Grid>
        <Grid item md={5} xs={12}>
          <FormInputText
            select
            id="displayOrder"
            label={t("banners.modal.positionLabel")}
            defaultValue={bannerData?.displayOrder}
            {...register("displayOrder")}
            error={errors?.displayOrder}
            disabled={loading}
          >
            {displayOrderList.map((orderNumber: string) => (
              <MenuItem key={orderNumber} value={orderNumber}>
                {orderNumber}
              </MenuItem>
            ))}
          </FormInputText>
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <p style={styles.titleModal}>
            {t("banners.modal.positionStatusTitle")}
          </p>
          <p style={styles.subTitleModal}>
            {t("banners.modal.positionStatusSubtitle")}
          </p>
        </Grid>
        <Grid item>
          <FormControlLabel
            control={
              <Switch
                id="active"
                defaultChecked={isEdition ? bannerData?.active : true}
                {...register("active")}
              />
            }
            label=""
          />
        </Grid>
      </Grid>

      {HorizontalLine}

      <p style={{ fontSize: 12, marginBottom: 30 }}>
        {t("revalidation.edit.requiredFields")}
      </p>

      {loading ? (
        <CircularProgress />
      ) : (
        <div style={styles.buttonContent}>
          {isEdition ? (
            <>
              <div style={styles.buttonItem}>
                <ActionButtons
                  typeButton="submit"
                  saveName={t("button.Save")}
                  enableLine={false}
                />
              </div>
              <Button
                style={{ height: 38 }}
                variant="contained"
                color="error"
                onClick={() => setOpenDeleteModal(true)}
              >
                {t("button.Exclude")}
              </Button>
            </>
          ) : (
            <div style={styles.buttonItem}>
              <ActionButtons
                typeButton="submit"
                saveName={t("button.Add")}
                enableLine={false}
              />
            </div>
          )}
        </div>
      )}

      {openDeleteModal && (
        <MyModal
          title={t("banners.modal.deleteModalTitle")}
          modalChange={setOpenDeleteModal}
          disableDivider
          maxWidth="32rem"
        >
          <Typography marginTop={3}>
            {t("banners.modal.deleteModalDescription")}
          </Typography>
          <Typography marginTop={0}>
            {t("banners.modal.deleteModalDescription2")}
          </Typography>

          {loading ? (
            <CircularProgress />
          ) : (
            <Stack
              alignItems="flex-start"
              direction="row"
              gap={2}
              marginTop={3}
            >
              <Button
                variant="contained"
                sx={{ width: "100px" }}
                onClick={() => deleteBanner()}
              >
                {t("button.Exclude")}
              </Button>
              <Button
                variant="outlined"
                sx={{ width: "100px", color: "#00B0F0" }}
                onClick={() => setOpenDeleteModal(false)}
              >
                {t("button.Cancel")}
              </Button>
            </Stack>
          )}
        </MyModal>
      )}
    </MyModal>
  );
}